import { ref, computed } from 'vue';
import { useTurnosStore } from '@/stores/turnos';
import { useUsuariosStore } from '@/stores/usuarios';

const NOTIFICACION_HORA = 8; // 8:00 AM
const STORAGE_KEY = 'ultima-notificacion-enviada';

export function useNotificaciones() {
  const permisoNotificaciones = ref('Notification' in window ? Notification.permission : 'denied');
  const turnosStore = useTurnosStore();
  const usuariosStore = useUsuariosStore();

  // Verificar si el usuario tiene notificaciones habilitadas
  const notificacionesHabilitadas = computed(() => {
    return usuariosStore.usuarioActual?.notificacionesHabilitadas === true;
  });

  /**
   * Solicitar permiso de notificaciones al navegador
   */
  const solicitarPermiso = async () => {
    if (!('Notification' in window)) {
      console.warn('Este navegador no soporta notificaciones');
      return false;
    }

    if (Notification.permission === 'granted') {
      permisoNotificaciones.value = 'granted';
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      permisoNotificaciones.value = permission;
      return permission === 'granted';
    }

    return false;
  };

  /**
   * Obtener turnos del día actual
   */
  const obtenerTurnosHoy = () => {
    const hoy = new Date();
    const y = hoy.getFullYear();
    const m = String(hoy.getMonth() + 1).padStart(2, '0');
    const d = String(hoy.getDate()).padStart(2, '0');
    const fechaHoy = `${y}-${m}-${d}`;

    return turnosStore.turnos.filter(turno => {
      if (!turno.fecha) return false;
      return turno.fecha === fechaHoy;
    });
  };

  /**
   * Crear notificación con resumen de turnos del día
   */
  const enviarNotificacionResumenDiario = () => {
    if (!notificacionesHabilitadas.value) {
      console.log('Notificaciones no habilitadas para este usuario');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.log('No hay permiso para notificaciones');
      return;
    }

    const turnosHoy = obtenerTurnosHoy();

    if (turnosHoy.length === 0) {
      // No enviar notificación si no hay turnos
      return;
    }

    const titulo = `Agenda del día - ${turnosHoy.length} ${turnosHoy.length === 1 ? 'turno' : 'turnos'}`;
    
    let cuerpo = '';
    if (turnosHoy.length <= 3) {
      // Mostrar detalles de cada turno si son pocos
      cuerpo = turnosHoy.map(t => `${t.inicio} - ${t.paciente}`).join('\n');
    } else {
      // Solo resumen si son muchos
      const primero = turnosHoy[0];
      const ultimo = turnosHoy[turnosHoy.length - 1];
      cuerpo = `Primer turno: ${primero.inicio}\nÚltimo turno: ${ultimo.inicio}`;
    }

    // Crear la notificación
    const notificacion = new Notification(titulo, {
      body: cuerpo,
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      tag: 'resumen-diario',
      requireInteraction: false,
      silent: false,
    });

    // Registrar que se envió la notificación
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());

    // Al hacer clic, enfocar la ventana
    notificacion.onclick = () => {
      window.focus();
      notificacion.close();
    };

    // Auto-cerrar después de 10 segundos
    setTimeout(() => {
      notificacion.close();
    }, 10000);
  };

  /**
   * Verificar si ya se envió la notificación hoy
   */
  const yaSeEnvioHoy = () => {
    const ultimaNotificacion = localStorage.getItem(STORAGE_KEY);
    if (!ultimaNotificacion) return false;

    const fechaUltima = new Date(ultimaNotificacion);
    const hoy = new Date();

    return (
      fechaUltima.getDate() === hoy.getDate() &&
      fechaUltima.getMonth() === hoy.getMonth() &&
      fechaUltima.getFullYear() === hoy.getFullYear()
    );
  };

  /**
   * Verificar si es hora de enviar notificación (8:00 AM)
   */
  const esHoraDeNotificar = () => {
    const ahora = new Date();
    const hora = ahora.getHours();
    const minutos = ahora.getMinutes();

    // Enviar entre 8:00 y 8:05
    return hora === NOTIFICACION_HORA && minutos < 5;
  };

  /**
   * Iniciar el sistema de notificaciones
   * Verifica cada minuto si debe enviar la notificación
   */
  const iniciarSistemaNotificaciones = async () => {
    // Solo iniciar si el usuario tiene notificaciones habilitadas
    if (!notificacionesHabilitadas.value) {
      console.log('Sistema de notificaciones no habilitado para este usuario');
      return;
    }

    // Solicitar permiso si no lo tiene
    const tienePermiso = await solicitarPermiso();
    if (!tienePermiso) {
      console.log('No se otorgó permiso para notificaciones');
      return;
    }

    // Verificar inmediatamente si debe notificar
    verificarYNotificar();

    // Verificar cada minuto
    const intervalo = setInterval(() => {
      verificarYNotificar();
    }, 60000); // 60 segundos

    return intervalo;
  };

  /**
   * Verificar y enviar notificación si corresponde
   */
  const verificarYNotificar = () => {
    if (!notificacionesHabilitadas.value) return;
    if (yaSeEnvioHoy()) return;
    if (!esHoraDeNotificar()) return;

    enviarNotificacionResumenDiario();
  };

  /**
   * Enviar notificación de prueba (para testing)
   */
  const enviarNotificacionPrueba = async () => {
    // Verificar soporte de notificaciones
    if (!('Notification' in window)) {
      return { exito: false, mensaje: 'Tu navegador no soporta notificaciones' };
    }

    // Solicitar permiso
    const tienePermiso = await solicitarPermiso();
    if (!tienePermiso) {
      return { exito: false, mensaje: 'Debes permitir las notificaciones en la configuración del navegador' };
    }

    try {
      const turnosHoy = obtenerTurnosHoy();
      const cantidad = turnosHoy.length;

      let titulo = '';
      let cuerpo = '';

      if (cantidad === 0) {
        titulo = '🩺 Agenda del día';
        cuerpo = 'No tienes turnos agendados para hoy';
      } else {
        titulo = `🩺 Tienes ${cantidad} ${cantidad === 1 ? 'turno' : 'turnos'} para hoy`;
        // Mostrar todos los turnos
        cuerpo = turnosHoy.map((t, index) => `${index + 1}. ${t.inicio} - ${t.paciente}`).join('\n');
      }

      // Intentar usar Service Worker si está disponible (mejor para móviles)
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(titulo, {
          body: cuerpo,
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: 'agenda-diaria',
          requireInteraction: false,
          vibrate: [200, 100, 200],
        });
      } else {
        // Fallback a Notification API directa (para escritorio)
        const notificacion = new Notification(titulo, {
          body: cuerpo,
          icon: '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: 'agenda-diaria',
          requireInteraction: false,
          silent: false,
        });

        notificacion.onclick = () => {
          window.focus();
          notificacion.close();
        };

        setTimeout(() => notificacion.close(), 10000);
      }

      return { exito: true, mensaje: 'Notificación enviada correctamente' };
    } catch (error) {
      console.error('Error al enviar notificación:', error);
      return { 
        exito: false, 
        mensaje: 'Error al enviar la notificación. Asegúrate de tener permisos activados.' 
      };
    }
  };

  return {
    permisoNotificaciones,
    notificacionesHabilitadas,
    solicitarPermiso,
    iniciarSistemaNotificaciones,
    enviarNotificacionPrueba,
  };
}
